/**
 * Data layer for the Admin panel's Breathwork section, backed by the
 * /admin/v1/breathwork-hub/* endpoints.
 *
 * ENVELOPE TOLERANCE: the current backend returns list payloads as a bare
 * array in `data` with totalCount in `meta` extras; the in-flight backend
 * task (session ledger prompt) aligns it with the Sleep Hub convention
 * ({ data, totalCount } inside `data`). Every list fetcher here normalizes
 * BOTH shapes so the UI works before and after that change lands.
 *
 * The two dashboard adapters below are exported for the company panel's data
 * layer (companyBreathworkApi.js): both panels are served by the same builders
 * in services/breathworkHubService.js, so they read the payload the same way
 * and the two pages can render the same panels.
 */

import { Api } from '../../api';

// `{ data, meta: { code: 1 } }` → { data, meta } (meta kept for extras).
const unwrapWithMeta = (response) => {
  if (response?.data?.meta?.code === 1) {
    return { data: response.data.data, meta: response.data.meta };
  }
  throw new Error(response?.data?.meta?.message || 'Request failed');
};

// Normalize a list payload to { rows, totalCount } across both envelope shapes.
const normalizeList = ({ data, meta }) => {
  if (Array.isArray(data)) {
    return { rows: data, totalCount: meta?.totalCount ?? data.length };
  }
  return { rows: data?.data || [], totalCount: data?.totalCount ?? meta?.totalCount ?? 0 };
};

/** Shared shaping for the sessions (tracker) dashboards — admin + company. */
export const adaptSessions = (data) => {
  const dailyTrend = data.dailyTrend || [];

  return {
    totalSessions: data.totalSessions ?? 0,
    holdSessions: data.holdSessions ?? 0,
    exhaleSessions: data.exhaleSessions ?? 0,
    usersActive: data.usersActive ?? 0,

    totalSessionTimeMins: data.totalSessionTimeMins ?? 0,
    holdTimeMins: data.holdTimeMins ?? 0,
    exhaleTimeMins: data.exhaleTimeMins ?? 0,
    avgSessionSeconds: data.avgSessionSeconds ?? 0,
    longestSessionSeconds: data.longestSessionSeconds ?? 0,

    meaningfulSessions: data.meaningfulSessions ?? 0,
    meaningfulPercentage: data.meaningfulPercentage ?? 0,
    meaningfulThresholdSeconds: data.meaningfulThresholdSeconds ?? 20,

    profiledUsers: data.profiledUsers ?? 0,
    avgStreak: data.avgStreak ?? 0,
    bestStreak: data.bestStreak ?? 0,
    avgMaxHold: data.avgMaxHold ?? 0,
    avgMaxExhale: data.avgMaxExhale ?? 0,
    bestHold: data.bestHold ?? 0,
    bestExhale: data.bestExhale ?? 0,
    commonPracticeTime: data.commonPracticeTime || null,

    // Backend zero-fills the last 30 days; charts consume plain series.
    sessionsPerDay: dailyTrend.map((day) => day.sessions ?? 0),
    holdPerDay: dailyTrend.map((day) => day.holdSessions ?? 0),
    exhalePerDay: dailyTrend.map((day) => day.exhaleSessions ?? 0),
    minutesPerDay: dailyTrend.map((day) => day.minutes ?? 0),

    sessionLengthDistribution: data.sessionLengthDistribution || [],
    timeOfDayDistribution: data.timeOfDayDistribution || [],
    weekdayPattern: data.weekdayPattern || [],
    practiceConsistency: data.practiceConsistency || [],

    experienceDistribution: data.experienceDistribution || [],
    goalsDistribution: data.goalsDistribution || [],
    streakDistribution: data.streakDistribution || [],
    holdDistribution: data.holdDistribution || [],
    exhaleDistribution: data.exhaleDistribution || []
  };
};

/** Shared shaping for the audio dashboards — admin + company. */
export const adaptListening = (data) => ({
  listeners: data.listeners ?? 0,
  totalPlays: data.totalPlays ?? 0,
  totalPlayTimeMins: data.totalPlayTimeMins ?? 0,
  avgSessionMins: data.avgSessionMins ?? 0,
  avgPlaysPerListener: data.avgPlaysPerListener ?? 0,
  mostPlayedBreathwork: data.mostPlayedBreathwork || null,
  topTenSharePercentage: data.topTenSharePercentage ?? 0,
  mostPlayed: (data.mostPlayed || []).map((item) => ({
    id: item._id,
    name: item.name,
    plays: item.plays,
    listeners: item.listeners ?? null,
    playTimeMins: item.playTimeMins,
    rating: item.rating ?? null
  })),
  playsTrend: (data.playsTrend || []).map((day) => day.plays),
  listenMinsTrend: (data.playsTrend || []).map((day) => day.listenMins || 0),
  weekdayPlays: data.weekdayPlays || [],
  sessionLengthDistribution: data.sessionLengthDistribution || [],
  listenerEngagement: data.listenerEngagement || []
});

export const fetchBreathworkAudioDashboard = async () => {
  try {
    const { data } = unwrapWithMeta(await Api.getBreathworkAudioDashboard());
    return {
      ...adaptListening(data),
      // Library half — super admin only; the company panel has no view of the
      // catalogue, only of what its people played.
      totalBreathworks: data.totalBreathworks ?? 0,
      avgRating: data.avgRating ?? 0,
      ratedBreathworks: data.ratedBreathworks ?? 0,
      unplayedBreathworks: data.unplayedBreathworks ?? 0,
      unplayedPercentage: data.unplayedPercentage ?? 0,
      ratingDistribution: data.ratingDistribution || [],
      authorSplit: data.authorSplit || []
    };
  } catch (error) {
    console.error('fetchBreathworkAudioDashboard error:', error);
    return null;
  }
};

export const fetchBreathworkSessionsDashboard = async () => {
  try {
    return adaptSessions(unwrapWithMeta(await Api.getBreathworkSessionsDashboard()).data);
  } catch (error) {
    console.error('fetchBreathworkSessionsDashboard error:', error);
    return null;
  }
};

export const fetchBreathworkCompanies = async ({ page = 1, perPage = 10, search = '' } = {}) => {
  const { rows, totalCount } = normalizeList(
    unwrapWithMeta(await Api.getBreathworkHubCompanies(page, perPage, search))
  );
  return {
    data: rows.map((company) => ({
      ...company,
      // The table's partner-profile cell reads the logo from company_logo.
      company_logo: company.companyLogo || null
    })),
    totalCount
  };
};

export const fetchBreathworkCompanyDetail = async (companyId) => {
  try {
    const { data } = unwrapWithMeta(await Api.getBreathworkHubCompanyDetail(companyId));
    return { company: data.company || null };
  } catch (error) {
    console.error('fetchBreathworkCompanyDetail error:', error);
    return { company: null };
  }
};

export const fetchBreathworkUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  accountType = '',
  companyId = ''
} = {}) => {
  const { rows, totalCount } = normalizeList(
    unwrapWithMeta(
      await Api.getBreathworkHubUsers(page, perPage, search, accountType, companyId)
    )
  );
  return { data: rows, totalCount };
};

export const fetchBreathworkUserDetail = async (userId) => {
  try {
    const { data } = unwrapWithMeta(await Api.getBreathworkHubUserDetail(userId));
    return data;
  } catch (error) {
    console.error('fetchBreathworkUserDetail error:', error);
    return null;
  }
};

// Shared formatting helpers -------------------------------------------------

/**
 * Breath times are seconds, and the interesting range runs from a few seconds
 * to a couple of minutes — so this stays in seconds until a minute is up,
 * rather than rounding a 45-second hold down to "0m".
 */
export const secsToMmSs = (secs) => {
  if (secs == null || isNaN(secs)) return '-';
  const rounded = Math.round(secs);
  if (rounded < 60) return `${rounded}s`;
  const m = Math.floor(rounded / 60);
  const s = rounded % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
};
